import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import BookingModal from './BookingModal';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const location = useLocation();
  const isHomePage = location.pathname === '/';
  const isRepairPage = location.pathname === '/repair-estimate';

  const navItems = [
    { name: 'About', href: isHomePage ? '#about' : '/#about' },
    { name: 'Services', href: isHomePage ? '#services' : '/#services' },
    { name: 'Watches', href: isHomePage ? '#watches' : '/#watches' },
    { name: 'Repair Estimate', href: '/repair-estimate', isRoute: true },
    { name: 'Contact', href: isHomePage ? '#contact' : '/#contact' }
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Force scrolled state for repair estimate page, but keep scroll animation
  const shouldShowScrolledState = isScrolled || isRepairPage;
  // Use non-scrolled text styling for repair page
  const shouldUseScrolledTextStyling = isScrolled;

  return (
    <header className={`fixed w-full top-0 z-50 transition-all duration-500 ${
      shouldShowScrolledState ? 'bg-charcoal/95 backdrop-blur-md shadow-2xl' : 'bg-transparent'
    } ${shouldUseScrolledTextStyling ? 'py-3' : 'py-6'}`}>
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="flex justify-between items-center">
          <div className="flex items-center">
            <Link to="/" className="relative">
              <h1 className={`font-playfair font-bold tracking-wider transition-all duration-500 ${
                shouldUseScrolledTextStyling ? 'text-2xl text-gold-300' : 'text-3xl text-white'
              }`}>
                MASTER
                <span className="block text-xs font-montserrat font-light tracking-super-wide mt-1">
                  JEWELERS
                </span>
              </h1>
              <div className="absolute -top-2 -right-4 text-gold-400 text-xl animate-sparkle">✦</div>
            </Link>
          </div>

          <nav className="hidden lg:flex items-center space-x-10">
            {navItems.map((item, index) => (
              item.isRoute ? (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`relative font-montserrat text-sm tracking-wider transition-all duration-300 hover:text-gold-400 ${
                    shouldUseScrolledTextStyling ? 'text-white' : 'text-white/90'
                  } group`}
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <span className="relative z-10">{item.name.toUpperCase()}</span>
                  <span className="absolute inset-x-0 -bottom-1 h-px bg-gradient-gold scale-x-0 group-hover:scale-x-100 transition-transform duration-300"></span>
                </Link>
              ) : (
                <a
                  key={item.name}
                  href={item.href}
                  className={`relative font-montserrat text-sm tracking-wider transition-all duration-300 hover:text-gold-400 ${
                    shouldUseScrolledTextStyling ? 'text-white' : 'text-white/90'
                  } group`}
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <span className="relative z-10">{item.name.toUpperCase()}</span>
                  <span className="absolute inset-x-0 -bottom-1 h-px bg-gradient-gold scale-x-0 group-hover:scale-x-100 transition-transform duration-300"></span>
                </a>
              )
            ))}
            <button 
              onClick={() => setIsBookingModalOpen(true)}
              className={`ml-6 px-6 py-2 font-montserrat text-sm tracking-wider border transition-all duration-300 hover:scale-105 ${
                shouldUseScrolledTextStyling 
                  ? 'border-gold-400 text-gold-400 hover:bg-gold-400 hover:text-charcoal' 
                  : 'border-white text-white hover:bg-white hover:text-charcoal'
              }`}
            >
              BOOK APPOINTMENT
            </button>
          </nav>

          <div className="lg:hidden">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className={`relative w-8 h-8 flex flex-col justify-center items-center transition-colors ${
                shouldUseScrolledTextStyling ? 'text-gold-400' : 'text-white'
              }`}
            >
              <span className={`block w-6 h-0.5 transition-all duration-300 ${
                shouldUseScrolledTextStyling ? 'bg-gold-400' : 'bg-white'
              } ${isMenuOpen ? 'rotate-45 translate-y-1.5' : ''}`}></span>
              <span className={`block w-6 h-0.5 my-1 transition-all duration-300 ${
                shouldUseScrolledTextStyling ? 'bg-gold-400' : 'bg-white'
              } ${isMenuOpen ? 'opacity-0' : ''}`}></span>
              <span className={`block w-6 h-0.5 transition-all duration-300 ${
                shouldUseScrolledTextStyling ? 'bg-gold-400' : 'bg-white'
              } ${isMenuOpen ? '-rotate-45 -translate-y-1.5' : ''}`}></span>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <div className={`lg:hidden absolute inset-x-0 top-full transition-all duration-500 ${
          isMenuOpen ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4 pointer-events-none'
        }`}>
          <div className="bg-charcoal/95 backdrop-blur-md shadow-2xl">
            <div className="px-6 py-8 space-y-6">
              {navItems.map((item) => (
                item.isRoute ? (
                  <Link
                    key={item.name}
                    to={item.href}
                    className="block font-montserrat text-white text-center text-lg tracking-wider hover:text-gold-400 transition-colors"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {item.name.toUpperCase()}
                  </Link>
                ) : (
                  <a
                    key={item.name}
                    href={item.href}
                    className="block font-montserrat text-white text-center text-lg tracking-wider hover:text-gold-400 transition-colors"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {item.name.toUpperCase()}
                  </a>
                )
              ))}
              <button 
                onClick={() => {
                  setIsBookingModalOpen(true);
                  setIsMenuOpen(false);
                }}
                className="w-full mt-6 px-6 py-3 font-montserrat text-sm tracking-wider border border-gold-400 text-gold-400 hover:bg-gold-400 hover:text-charcoal transition-all duration-300"
              >
                BOOK APPOINTMENT
              </button>
            </div>
          </div>
        </div>
      </div>
      
      <BookingModal
        isOpen={isBookingModalOpen}
        onClose={() => setIsBookingModalOpen(false)}
        isFromRepair={false}
      />
    </header>
  );
};

export default Header;